import { Link } from 'react-router-dom';
import { ArrowRight, Layers, Brain, Zap, TrendingDown, Globe, Shield } from 'lucide-react';

// Static mini architecture diagram for the featured card
const MiniArchitectureDiagram = () => {
    const workloads = ['Agentic', 'Real-time', 'Batch'];
    const gpus = ['H100', 'A100', 'L40S', 'MI300X'];

    return (
        <div className="flex flex-col items-center gap-2.5 py-2">
            {/* Workload chips */}
            <div className="flex items-center gap-1.5">
                {workloads.map(w => (
                    <div key={w} className="px-2.5 py-1 rounded-md border border-white/10 bg-white/[0.03] text-[10px] font-mono text-gray-400">
                        {w}
                    </div>
                ))}
            </div>

            {/* Flow line */}
            <div className="w-0.5 h-4 bg-volt-500/30 rounded-full" />

            {/* Forecaster box */}
            <div className="px-6 py-2.5 rounded-lg border border-volt-500/30 bg-volt-500/[0.05]">
                <div className="text-[10px] font-bold text-volt-400 text-center tracking-wide">FORECASTER</div>
                <div className="flex gap-2 mt-1">
                    {['Predict', 'Route', 'Cache', 'Scale'].map(s => (
                        <span key={s} className="text-[9px] text-gray-500">{s}</span>
                    ))}
                </div>
            </div>

            {/* Flow line */}
            <div className="w-0.5 h-4 bg-volt-500/30 rounded-full" />

            {/* GPU chips */}
            <div className="flex items-center gap-1.5">
                {gpus.map(g => (
                    <div key={g} className="px-2 py-1 rounded-md border border-white/10 bg-white/[0.03] text-[10px] font-mono text-gray-500">
                        {g}
                    </div>
                ))}
            </div>
        </div>
    );
};

const featuredHighlight = {
    icon: Layers,
    title: 'Architecture',
    description: 'Where Forecaster sits — the intelligence layer between your runtimes and GPU infrastructure.',
    link: '/architecture',
};

const highlights = [
    {
        icon: Brain,
        title: 'Intelligence',
        description: 'Predictive scaling that learns your traffic patterns. GPUs warm and ready before demand hits.',
        link: '/how-it-works',
    },
    {
        icon: Zap,
        title: 'Runtime',
        description: 'Cold starts, KV cache OOM, low GPU utilization, energy waste. How Forecaster fixes each one.',
        link: '/runtime',
    },
    {
        icon: TrendingDown,
        title: 'Economics',
        description: '40-60% lower GPU costs through multi-cloud arbitrage, spot mixing, and GPU hibernation.',
        link: '/economics',
    },
    {
        icon: Globe,
        title: 'Platform',
        description: '12 cloud providers, 8 accelerator types, 4 inference frameworks. One control plane.',
        link: '/platform',
    },
    {
        icon: Shield,
        title: 'AI Factory',
        description: 'Declare intent — sovereignty, budgets, SLOs, fault tolerance. Forecaster enforces it.',
        link: '/factory',
    },
];

export const FeatureHighlights = () => {
    return (
        <section className="py-20 md:py-28 px-4 sm:px-6 bg-obsidian-900">
            <div className="max-w-5xl mx-auto">
                <div className="text-center mb-14">
                    <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                        One Intelligence Layer. Every Cloud. Every Chip.
                    </h2>
                    <p className="text-gray-400 max-w-2xl mx-auto">
                        Forecaster predicts demand, routes intelligently, and orchestrates across your entire GPU fleet.
                    </p>
                </div>

                {/* Featured Architecture card */}
                <Link
                    to={featuredHighlight.link}
                    className="group block mb-6 p-6 md:p-8 rounded-2xl border border-volt-500/20 bg-white/[0.02] hover:border-volt-500/40 transition-all"
                >
                    <div className="flex flex-col lg:flex-row items-start lg:items-center gap-6">
                        <div className="flex-1">
                            <div className="flex items-start gap-4">
                                <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-volt-500/10 flex-shrink-0">
                                    <featuredHighlight.icon className="w-5 h-5 text-volt-400" />
                                </div>
                                <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-2">
                                        <h3 className="text-lg font-semibold text-white">{featuredHighlight.title}</h3>
                                        <ArrowRight className="w-4 h-4 text-gray-600 group-hover:text-volt-400 group-hover:translate-x-1 transition-all" />
                                    </div>
                                    <p className="text-sm text-gray-400 leading-relaxed">{featuredHighlight.description}</p>
                                </div>
                            </div>
                        </div>
                        <div className="flex-shrink-0 hidden lg:block">
                            <MiniArchitectureDiagram />
                        </div>
                    </div>
                </Link>

                {/* Remaining cards */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {highlights.map((item, i) => (
                        <Link
                            key={i}
                            to={item.link}
                            className="group p-6 rounded-2xl border border-white/[0.06] bg-white/[0.02] hover:border-volt-500/30 transition-all"
                        >
                            <div className="flex items-start gap-4">
                                <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-volt-500/10 flex-shrink-0">
                                    <item.icon className="w-5 h-5 text-volt-400" />
                                </div>
                                <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-2">
                                        <h3 className="text-lg font-semibold text-white">{item.title}</h3>
                                        <ArrowRight className="w-4 h-4 text-gray-600 group-hover:text-volt-400 group-hover:translate-x-1 transition-all" />
                                    </div>
                                    <p className="text-sm text-gray-400 leading-relaxed">{item.description}</p>
                                </div>
                            </div>
                        </Link>
                    ))}
                </div>
            </div>
        </section>
    );
};
